import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SupportHubStore } from '../support-hub.store';
import { MfgappsEventService } from '../../../services/mfgapps-event.service';

@Component({
  selector: 'app-support-hub',
  standalone: true,
  imports: [FormsModule, AsyncPipe, RouterLink, RouterOutlet, RouterLinkActive],
  templateUrl: './support-hub.component.html',
  styleUrl: './support-hub.component.scss'
})
export class SupportHubComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  currentUserRole$;
  selectedRole: string = SupportHubStore.ROLE_USER;
  selectedUseCaseId: number | null = null;
  hideViewAsOption: boolean = false;
  disableusecaseSelect: boolean = false;

  get roleOptions(): string[] {
    return this.store.uniqueRoleNames;
  }

  get useCaseOptions() {
    return this.store.useCaseOptions;
  }

  constructor(
    private readonly store: SupportHubStore,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly eventService: MfgappsEventService
  ) {
    this.currentUserRole$ = this.store.currentUserRoleSubject;
  }

  ngOnInit() {
    this.environmentalUIConfiguration();

    this.store.selectedUseCaseIdSubject.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(useCaseId => {
      this.selectedUseCaseId = useCaseId;
    });

    this.currentUserRole$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(role => {
      this.selectedRole = role;
    });

    void (async () => {
      await this.store.loadLookup();
     
      this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(queryParams => {
        const shouldIncludeUseCase = this.shouldIncludeUseCaseQueryParam();
        const currentUseCaseCode = queryParams.get('usecase')?.trim().toUpperCase() ?? '';

        if (!shouldIncludeUseCase) {
          if (currentUseCaseCode) {
            this.router.navigate([], {
              relativeTo: this.route,
              queryParams: this.getRemainingQueryParams(queryParams.keys),
              replaceUrl: true
            });
          }
          return;
        }

        const selectedUseCaseId = this.store.setSelectedUseCaseFromQuery(queryParams.get('usecase'));
        const selectedUseCaseCode = this.store.getUseCaseCodeById(selectedUseCaseId);
        if (!selectedUseCaseCode || currentUseCaseCode === selectedUseCaseCode) {
          return;
        }
        const nextQueryParams = this.store.getUseCaseQueryParams(this.getRemainingQueryParams(queryParams.keys));
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: nextQueryParams,
          replaceUrl: true
        });
      });
    })();
  }

  private environmentalUIConfiguration(): void {
    this.hideViewAsOption = !this.eventService.isDevelopmentEnvironment;
    this.disableusecaseSelect = !this.eventService.isDevelopmentEnvironment;
  }
  
  
  onRoleDropdownChange(role: string) {
    this.selectedRole = role;
    this.store.setCurrentUserRole(role);
  }

  onUseCaseDropdownChange(useCaseId: number | null): void {
    this.store.setSelectedUseCaseId(useCaseId);
    const nextQueryParams = this.shouldIncludeUseCaseQueryParam()
      ? this.store.getUseCaseQueryParams(this.getRemainingQueryParams())
      : this.getRemainingQueryParams();
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: nextQueryParams,
      replaceUrl: true
    });
  }

  getNavQueryParams(): Record<string, string | number> {
    return this.store.getUseCaseQueryParams();
  }

  private getRemainingQueryParams(queryKeys?: string[]): Record<string, string | number> {
    const params = this.route.snapshot.queryParamMap;
    const keys = queryKeys ?? params.keys;
    const remainingParams: Record<string, string | number> = {};
    keys.forEach(key => {
      if (key === 'usecase') {
        return;
      }
      const value = params.get(key);
      if (value != null && value !== '') {
        remainingParams[key] = value;
      }
    });
    return remainingParams;
  }

  private shouldIncludeUseCaseQueryParam(): boolean {
    const path = this.router.url.split('?')[0].toLowerCase();
    return path.endsWith('/support-hub/dashboard')
      || path.endsWith('/support-hub/all-tickets')
      || path.endsWith('/support-hub/version')
      || path.endsWith('/support-hub/release')
      || path.endsWith('/support-hub/role-assignment');
  }

  getRoleLabel(role: any): string {
    return role;
  }

  getRoleClass(role: any): string {
    if (!role) {
      return '';
    }
    return 'role-' + (role as string).toLowerCase().replace(/\s+/g, '-');
  }

  isAdminRole(role: unknown): boolean {
    return this.store.isAdminRoleName(role as string);
  }
}